import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generateAndDownloadPdf } from "@/lib/pdfExport";
import { getTranscriptText } from "@/lib/transcript";
import { supabase } from "@/lib/supabase";
import type { Meeting, Summary, Transcript } from "@/types";

interface ExportActionsProps {
  meeting: Meeting;
  summary: Summary;
  transcript: Transcript | null;
}

export function ExportActions({
  meeting,
  summary,
  transcript,
}: ExportActionsProps) {
  const { t } = useTranslation();
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingDocx, setLoadingDocx] = useState(false);

  const handleDocx = async (withTranscript: boolean) => {
    setLoadingDocx(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-docx`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({
          meeting_id: meeting.id,
          include_transcript: withTranscript,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download =
        (meeting.title ?? "compte-rendu").replace(/[^\w\-]+/g, "_") + ".docx";
      a.click();
      URL.revokeObjectURL(dlUrl);
    } catch {
      alert(t("export.docxError"));
    } finally {
      setLoadingDocx(false);
    }
  };

  const handlePdf = async (withTranscript: boolean) => {
    setLoadingPdf(true);
    try {
      await generateAndDownloadPdf(
        meeting,
        summary,
        withTranscript ? transcript ?? undefined : undefined
      );
    } catch {
      alert(t("export.pdfError"));
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => handlePdf(false)}
        disabled={loadingPdf}
        className="px-4 py-2 bg-redi-primary text-white text-sm rounded-lg hover:bg-redi-primary/80 transition-colors disabled:opacity-50"
      >
        {loadingPdf ? "..." : t("export.pdfSummaryOnly")}
      </button>

      {transcript && (
        <button
          onClick={() => handlePdf(true)}
          disabled={loadingPdf}
          className="px-4 py-2 bg-redi-primary/20 text-redi-primary text-sm rounded-lg hover:bg-redi-primary/30 transition-colors disabled:opacity-50"
        >
          {t("export.pdfWithTranscript")}
        </button>
      )}

      {summary.content_text && (
        <button
          onClick={() => handleCopy(summary.content_text!)}
          className="px-4 py-2 bg-redi-surface text-redi-text text-sm rounded-lg hover:bg-white/10 transition-colors"
        >
          {t("export.shareSummary")}
        </button>
      )}

      {transcript && (
        <button
          onClick={() => handleCopy(getTranscriptText(transcript))}
          className="px-4 py-2 bg-redi-surface text-redi-text text-sm rounded-lg hover:bg-white/10 transition-colors"
        >
          {t("export.shareTranscript")}
        </button>
      )}

      <button
        onClick={() => handleDocx(false)}
        disabled={loadingDocx}
        className="px-4 py-2 bg-redi-surface text-redi-text text-sm rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
      >
        {loadingDocx ? "..." : t("export.docxSummaryOnly")}
      </button>
      {transcript && (
        <button
          onClick={() => handleDocx(true)}
          disabled={loadingDocx}
          className="px-4 py-2 bg-redi-surface text-redi-text text-sm rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
        >
          {t("export.docxWithTranscript")}
        </button>
      )}
    </div>
  );
}
